import React, { useEffect, useRef } from "react"
import clsx from "clsx"
import { LuChevronDown } from "react-icons/lu"
import { useHeaderMenu } from "../../context/HeaderMenuContext"

interface HeaderPopupProps {
  menuKey: string
  buttonLabel: string
  panel: React.ReactNode
  panelClassName?: string
  panelContainerClassName?: string
  transparent?: boolean
}

const CLOSE_DELAY_MS = 150

function HeaderPopup({
  menuKey,
  buttonLabel,
  panel,
  panelClassName,
  panelContainerClassName,
  transparent,
}: HeaderPopupProps) {
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const { hoveredMenu, setHoveredMenu, closeMenu } = useHeaderMenu()
  const isOpen = hoveredMenu === menuKey

  const openMenu = () => {
    clearTimeout(closeTimeout.current)
    setHoveredMenu(menuKey)
  }

  const scheduleClose = () => {
    clearTimeout(closeTimeout.current)
    closeTimeout.current = setTimeout(() => closeMenu(menuKey), CLOSE_DELAY_MS)
  }

  useEffect(() => {
    return () => clearTimeout(closeTimeout.current)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu(menuKey)
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isOpen, menuKey, closeMenu])

  return (
    <div onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        type="button"
        onClick={openMenu}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="relative z-20 focus-visible:ring-0 focus-visible:outline-0"
      >
        <div
          className={clsx(
            "font-medium cursor-pointer flex items-center gap-3 relative",
            "focus-visible:ring-0 focus-visible:outline-0",
            transparent ? "text-white font-medium" : "text-black-22 font-medium",
            isOpen && "text-new-blue font-semibold"
          )}
        >
          {buttonLabel}
          <LuChevronDown className={clsx(isOpen && "rotate-180 text-new-blue")} />
        </div>
      </button>

      {isOpen && (
        <div
          className={clsx(
            "z-10 absolute top-10 pt-8 left-0 w-screen drop-shadow-b-md",
            panelContainerClassName
          )}
        >
          <div className={clsx(panelClassName)}>
            <div className="bg-white py-8" onMouseEnter={openMenu}>
              {panel}
            </div>
            <div
              className="bg-black h-screen opacity-50"
              onMouseEnter={scheduleClose}
              onClick={() => closeMenu(menuKey)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default HeaderPopup
